import { FC, useCallback, useState } from "react";

import styles from "./styles.module.scss";
import Text from "../../../Text";

const SUB_TABS = [
    { key: "overview", label: "Vue d'ensemble" },
    { key: "rules", label: "Les 3 règles" },
    { key: "checklist", label: "Avant chaque trade" },
] as const;

type SubTabKey = (typeof SUB_TABS)[number]["key"];

const Method: FC = () => {
    const [activeSubTab, setActiveSubTab] = useState<SubTabKey>("overview");

    const handleSubTabChange = useCallback((key: SubTabKey) => {
        setActiveSubTab(key);
    }, []);

    return (
        <div className={styles.account_form}>
            <Text variant="title" className={styles.title}>
                La stratégie Objectif Trading
            </Text>

            <div className={styles.subTabs} role="tablist">
                {SUB_TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={activeSubTab === tab.key}
                        className={`${styles.subTab} ${
                            activeSubTab === tab.key ? styles.subTabActive : ""
                        }`}
                        onClick={() => handleSubTabChange(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeSubTab === "overview" && <Overview />}
            {activeSubTab === "rules" && <Rules />}
            {activeSubTab === "checklist" && <Checklist />}
        </div>
    );
};

const Overview: FC = () => (
    <div className={styles.body}>
        <Text variant="body" className={styles.lead}>
            Dans ce module, tu as appris à construire un plan de trading, à
            repérer le bon moment pour entrer et à placer ton stop-loss. Ce sont
            des outils. Ils t&apos;aident à trouver de bons trades. Mais la
            stratégie, elle, tient en trois règles.
        </Text>

        <div className={styles.compareGrid}>
            <div className={styles.compareCard}>
                <div className={styles.compareHeader}>
                    <span className={styles.compareBadge}>Les outils</span>
                    <h3 className={styles.compareTitle}>
                        Comment trouver un bon trade
                    </h3>
                </div>
                <ul className={styles.compareList}>
                    <li>Construire son plan de trading</li>
                    <li>Repérer le bon retournement pour entrer</li>
                    <li>Placer son stop-loss</li>
                </ul>
                <p className={styles.compareNote}>
                    Ils évoluent et s&apos;affinent avec l&apos;expérience.
                </p>
            </div>

            <div className={`${styles.compareCard} ${styles.compareCardAccent}`}>
                <div className={styles.compareHeader}>
                    <span
                        className={`${styles.compareBadge} ${styles.compareBadgeAccent}`}
                    >
                        La stratégie
                    </span>
                    <h3 className={styles.compareTitle}>
                        Les 3 règles qui ne changent jamais
                    </h3>
                </div>
                <ul className={styles.compareList}>
                    <li>Un plan dans lequel tu as confiance</li>
                    <li>
                        Un gain espéré au moins deux fois supérieur au risque
                        encouru sur le trade
                    </li>
                    <li>
                        Le respect du plan et du money management, 1&nbsp;%
                        maximum
                    </li>
                </ul>
                <p className={styles.compareNote}>
                    Elles s&apos;appliquent à chaque trade, sans exception.
                </p>
            </div>
        </div>

        <p className={styles.closingLine}>
            Les outils t&apos;aident à trouver de bons trades.
            <br />
            <strong>La stratégie te permet de durer.</strong>
        </p>
    </div>
);

const Rules: FC = () => (
    <div className={styles.body}>
        <Text variant="body" className={styles.sectionIntro}>
            Les 3 règles de la stratégie
        </Text>

        <section className={styles.ruleSection}>
            <h3 className={styles.ruleTitle}>
                Règle 1 — Trader avec un plan de trading dans lequel tu as
                confiance
            </h3>
            <p className={styles.ruleText}>
                C&apos;est la règle numéro un. Avant la séance, ton plan te dit
                dans quel sens tu veux travailler et quel objectif tu vises. Si
                tu n&apos;as pas confiance dans ton plan, tu ne trades pas.
            </p>
            <div className={styles.callout}>
                Un retournement, même parfait, ne remplace jamais un plan. Sans
                conviction sur la direction, il n&apos;y a pas de trade :{" "}
                <strong>ne rien faire est aussi une décision de trader.</strong>
            </div>
        </section>

        <section className={styles.ruleSection}>
            <h3 className={styles.ruleTitle}>
                Règle 2 — Chaque trade doit viser un gain au moins deux fois
                supérieur au risque
            </h3>
            <p className={styles.ruleText}>
                Chaque trade réalisé doit avoir un{" "}
                <strong>ratio d&apos;au moins 2 pour 1</strong> entre le
                stop-loss et l&apos;objectif : le gain espéré vaut au minimum
                deux fois le risque pris. Si ce n&apos;est pas le cas, tu ne
                prends pas le trade.
            </p>

            <div className={styles.ratioBlock}>
                <div className={styles.ratioRow}>
                    <span className={styles.ratioLabel}>
                        Risque (stop-loss)
                    </span>
                    <div
                        className={`${styles.ratioBar} ${styles.ratioBarRisk}`}
                    >
                        50&nbsp;$
                    </div>
                </div>
                <div className={styles.ratioRow}>
                    <span className={styles.ratioLabel}>
                        Gain espéré (objectif)
                    </span>
                    <div
                        className={`${styles.ratioBar} ${styles.ratioBarReward}`}
                    >
                        100&nbsp;$ minimum
                    </div>
                </div>
            </div>

            <div className={styles.callout}>
                <strong>Pourquoi c&apos;est si puissant :</strong> avec un ratio
                de 2 pour 1, tu es rentable dès que tu gagnes plus d&apos;un
                trade sur trois (hors frais). Tu n&apos;as pas besoin d&apos;avoir
                raison à chaque fois.
            </div>
        </section>

        <section className={styles.ruleSection}>
            <h3 className={styles.ruleTitle}>
                Règle 3 — Respecter son plan de trading et son money management
            </h3>
            <p className={styles.ruleText}>
                Une fois le plan établi, tu t&apos;y tiens. Et ton risque ne
                peut en aucun cas dépasser{" "}
                <strong>1&nbsp;% du capital tradé</strong>.
            </p>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Capital tradé</th>
                            <th>Risque maximum (1&nbsp;%)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>10&nbsp;000&nbsp;$</td>
                            <td className={styles.tableAccent}>100&nbsp;$</td>
                        </tr>
                        <tr>
                            <td>25&nbsp;000&nbsp;$</td>
                            <td className={styles.tableAccent}>250&nbsp;$</td>
                        </tr>
                        <tr>
                            <td>50&nbsp;000&nbsp;$</td>
                            <td className={styles.tableAccent}>500&nbsp;$</td>
                        </tr>
                        <tr>
                            <td>150&nbsp;000&nbsp;$</td>
                            <td className={styles.tableAccent}>
                                1&nbsp;500&nbsp;$
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className={styles.callout}>
                <strong>1&nbsp;% est un plafond, pas un objectif.</strong> Tant
                que tu n&apos;es pas régulier, reste bien en dessous.
            </div>
        </section>
    </div>
);

const Checklist: FC = () => (
    <div className={styles.body}>
        <Text variant="body" className={styles.pageHeading}>
            Avant chaque trade : les 3 questions
        </Text>
        <p className={styles.lead}>
            Pose-toi ces trois questions avant d&apos;appuyer sur le bouton.
        </p>

        <ol className={styles.questions}>
            <li className={styles.questionCard}>
                <span className={styles.questionIndex}>1</span>
                <p>
                    Est-ce que j&apos;ai confiance dans mon plan de trading&nbsp;?
                </p>
            </li>
            <li className={styles.questionCard}>
                <span className={styles.questionIndex}>2</span>
                <p>
                    Est-ce que le gain espéré vaut au moins deux fois mon risque
                    (ratio 2 pour 1)&nbsp;?
                </p>
            </li>
            <li className={styles.questionCard}>
                <span className={styles.questionIndex}>3</span>
                <p>
                    Est-ce que mon risque respecte mon money management, soit
                    1&nbsp;% maximum du capital tradé&nbsp;?
                </p>
            </li>
        </ol>

        <div className={`${styles.callout} ${styles.calloutWarn}`}>
            Une seule réponse est «&nbsp;non&nbsp;»&nbsp;?{" "}
            <strong>Pas de trade.</strong>
        </div>

        <p className={styles.closingLine}>
            Les outils t&apos;aident à trouver de bons trades.
            <br />
            <strong>La stratégie te permet de durer.</strong>
        </p>
    </div>
);

export default Method;
